/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export type CenterTab = "problems" | "history" | "sent";

interface CenterState {
  open: boolean;
  tab: CenterTab;
  /** An item to highlight (a problem name…), set by deep links and notifications. */
  focus: string | null;
}

interface CenterApi extends CenterState {
  openCenter: (opts?: { tab?: CenterTab; focus?: string | null }) => void;
  closeCenter: () => void;
  setTab: (tab: CenterTab) => void;
}

const Ctx = createContext<CenterApi | null>(null);

const TABS: CenterTab[] = ["problems", "history", "sent"];

/** A link from a notification (`?open=notifications&tab=sent&id=…`) opens the centre on load; the params are then dropped. */
function stateFromUrl(): CenterState {
  const closed: CenterState = { open: false, tab: "problems", focus: null };
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("open") !== "notifications") return closed;
    const tab = q.get("tab") as CenterTab | null;
    const focus = q.get("id");
    ["open", "tab", "id"].forEach((k) => q.delete(k));
    const rest = q.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
    return { open: true, tab: tab && TABS.includes(tab) ? tab : "problems", focus };
  } catch {
    return closed;
  }
}

/** Whether the notification centre (the side panel) is open, and on which tab. */
export const NotificationCenterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CenterState>(stateFromUrl);
  const openCenter = useCallback(
    (opts?: { tab?: CenterTab; focus?: string | null }) =>
      setState((s) => ({ open: true, tab: opts?.tab ?? s.tab, focus: opts?.focus ?? null })),
    [],
  );
  const closeCenter = useCallback(() => setState((s) => ({ ...s, open: false, focus: null })), []);
  const setTab = useCallback((tab: CenterTab) => setState((s) => ({ ...s, tab })), []);
  const value = useMemo(() => ({ ...state, openCenter, closeCenter, setTab }), [state, openCenter, closeCenter, setTab]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationCenter(): CenterApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useNotificationCenter needs <NotificationCenterProvider>");
  return v;
}
